// easyui-combogrid.component.ts
// (c) 2019 EvoluS Srl
// (a) VG

import {
  Component, Input, ViewChild, Injector, OnChanges, SimpleChanges, NgZone,
  ElementRef, forwardRef, AfterViewInit
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Globs } from '../globs';
import { EasyuiErrorService } from './easyui-error.service';

declare let $: any;

@Component({
  selector: 'easyui-combogrid',
  template: `
    <div style="position: relative;">
      <div #host></div>
      <div #tooltip hidden [className]="'tooltiptext'">{{tooltipText}}</div>
    </div>
  `,
  styleUrls: ['./easyui.common.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EasyuiCombogridComponent),
      multi: true
    }
  ]
})
export class EasyuiCombogridComponent implements AfterViewInit, OnChanges {
  private host: any;
  public onControlChange: Function;
  public onControlTouched: Function;
  private initialValue: string;
  private dataModel: string;
  private showTooltip = false;
  public tooltipText = '';
  private changingValue = false;
  private control: any;
  private subscription;
  private isFocused = false;
  @ViewChild('host') hostElement: ElementRef;
  @ViewChild('tooltip') tooltipElement: ElementRef;
  @Input() options: any = {};
  @Input() width: number;
  @Input() height: number;
  @Input() maxlength: number;
  @Input() minlength: number;
  @Input() nxUppercase: string;
  @Input() nxReadonly: string;
  @Input() catalogo: Array<any> = [];
  @Input() icons: Array<any>;

  constructor(private zone: NgZone, private injector: Injector, private error: EasyuiErrorService) { }

  public ngAfterViewInit() {
    // configura bind con form di angular
    this.control = this.injector.get(NgControl);
    // configura easyui
    this.configura(this.options);
    // valuta i cambiamenti di status
    if (this.control) {
      this.subscription = this.control.statusChanges.subscribe((newStatus: string) => {
        setTimeout(() => {
          this.checkTooltip();
        }, 0);
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    // // se si dovesse cambiare il catalogo in uso alla combo
    // if (changes.catalogo) {
    //   if (!changes.catalogo.firstChange) {
    //     this.host.combobox('loadData', changes.catalogo.currentValue)
    //   }
    // }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // la form notifica un cambiamento dal datamodel al controllo
  public writeValue(value: string) {
    if (this.host) {
      this.changingValue = true;
      this.host.combogrid('setValue', value);
      this.initialValue = value;
      this.dataModel = value;
      this.validateControl();
      this.changingValue = false;
    }
  }

  public registerOnChange(fn: any) {
    this.onControlChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onControlTouched = fn;
  }

  // metodi

  public setFocus() {
    const innerHost = this.host.combogrid('textbox');
    setTimeout(() => {
      innerHost.focus();
    }, 1);
  }

  public selectAll() {
    const innerHost = this.host.combogrid('textbox');
    const len = innerHost.val().length * 2;
    this.host.combogrid('setSelectionRange', { start: 0, end: len });
  }

  public showPanel() {
    this.host.combogrid('showPanel');
  }

  public hidePanel() {
    this.host.combogrid('hidePanel');
  }

  public query(val: string) {
    const options = this.host.combogrid('options');
    options.keyHandler.query.call(this.hostElement.nativeElement, val, null);
  }

  public setText(val: string) {
    if (val) {
      this.host.combogrid('setValue', val);
    }
  }

  public disable() {
    this.host.combogrid('disable');
  }

  public enable() {
    this.host.combogrid('enable');
  }

  public readonly(on: boolean) {
    this.host.combogrid('readonly', on);
  }

  public clearValue() {
    this.host.combogrid('clear');
  }

  public reset() {
    this.host.combogrid('reset');
  }

  public getDatagrid() {
    return this.host.combogrid('grid');
  }

  public loadData(dati) {
    this.host.combogrid('grid').datagrid('loadData', dati);
  }

  public styleElement(tag: string, css: any) {
    const element = this.host.find(tag);
    element.css(css);
  }

  // metodi privati

  private configura(options) {
    this.zone.runOutsideAngular(() => {
      // configura l'elemento combobox di easyui
      this.host = $(this.hostElement.nativeElement);
      // configura le icone (clear di default)
      const icons = [];
      // let icons = [{
      //   iconCls: 'icon-clear',
      //   handler: () => {
      //     this.host.combobox('setValue',null)
      //     // this.updateDatamodel()
      //     // this.setFocus()
      //   }
      // }]
      if (this.icons) {
        for (let i = 0; i < this.icons.length; i++) {
          icons.push(this.icons[i]);
        }
      }
      const localoptions = {
        width: this.width ? this.width : '100%',
        height: this.height ? this.height : Globs.UI_HEIGHT,
        icons,
        textField: 'disp',
        idField: 'val',
        editable: false,
        // data: this.catalogo,
        panelHeight: 110,
        onChange: (newValue, oldValue) => {
          if (this.changingValue) {
            // il cambiambento arriva dal datamodel
            this.changingValue = false;
          } else {
            // cambiamento dal component, aggiornare il datamodel
            this.control.control.markAsDirty();
            this.updateDatamodel();
            this.validateControl();
            this.setFocus();
          }
        }
        // onHidePanel: () => {
        //   this.setFocus() // VG: BugFix, do not remove
        // }
      };
      Object.assign(localoptions, options);
      // creare l'oggetto combobox
      this.host.combogrid(localoptions);
      // rimuovere href dalle icon (serve per evitare un fastidioso bug)
      const opts = this.host.combogrid('options');
      for (let i = 0; i < opts.icons.length; i++) {
        const icon = this.host.combogrid('getIcon', i);
        icon.removeAttr('href');
      }
      // individuare l'oggetto di input
      const innerHost = this.host.combogrid('textbox');

      innerHost.on('keydown', (event) => {
        // codice tastiera...
        // console.log('Keydown: ',event.which)
        switch (event.which) {
          case 40: // freccia giù
            this.host.combogrid('showPanel');
            break;
          case 46: // DEL
            this.host.combogrid('setValue', null);
            this.updateDatamodel();
            break;
          case 27: // ESC
            this.host.combogrid('hidePanel');
            if (this.control && this.control.dirty) {
              this.control.reset(this.initialValue);
              this.control.control.markAsPristine();
              this.control.control.markAsUntouched();
              event.stopPropagation();
            }
            break;
          case 13: // INVIO
            this.validateControl();
            break;
        }
      });
      innerHost.on('blur', () => {
        this.onfocusout();
      });
      innerHost.on('focus', () => {
        this.onfocusin();
      });
      innerHost.on('input', (event) => {
        if (this.showTooltip) { this.validateControl(); }
        this.updateDatamodel();
      });
      // settare le dimensioni del font nel campo di input
      innerHost.css('font-size', Globs.UI_FONTSIZE);
      // in caso sia presente l'attributo nxUppercase
      if (this.nxUppercase === '') {
        innerHost.css('text-transform', 'uppercase');
      }
      // in caso sia presente l'attribute nxReadonly
      if (this.nxReadonly === '') {
        this.host.textbox('readonly');
      }
      // per evitare la fastidiosa riga rossa, disabilitare lo spelling checker
      innerHost.attr('spellcheck', false);
      // evitare autocompletamento
      innerHost.attr('autocomplete', 'off');
      // settare anche il numero massimo di caratteri ammesso nell'input
      if (this.maxlength) {
        innerHost.attr('maxlength', this.maxlength);
      }
      this.showClearIcon(false);
    });
  }

  public onfocusin() {
    this.isFocused = true;
    this.checkTooltip();
    this.showClearIcon(true);
  }

  public onfocusout() {
    this.isFocused = false;
    this.checkTooltip();
    this.showClearIcon(false);
  }

  private showClearIcon(show: boolean) {
    // let icon = this.host.combobox('getIcon',0)
    // if (show) {
    //   icon.css('visibility', 'visible')
    // }
    // else {
    //   icon.css('visibility', 'hidden')
    // }
  }

  private updateDatamodel() {
    // prendere il contenuto dal campo di input e trimmarlo
    let viewmodel: string = this.host.textbox('getValue').trim();
    // maiuscolizzare se è il caso (NB: nella textbox resta minuscolo)
    if (this.nxUppercase === '') {
      viewmodel = viewmodel.toUpperCase();
    }
    // se stringa vuota il model diventa null
    if (viewmodel === '') {
      viewmodel = null;
    }
    // trasmetto il valore al datamodel della form
    if (this.dataModel !== viewmodel) {
      this.dataModel = viewmodel;
      this.zone.run(() => this.onControlChange(viewmodel));
    }
  }

  private validateControl() {
    // this.checkTooltip()
  }

  private checkTooltip() {
    // mostrare solo se il campo è stato modificato e se si è verificato un errore
    // la configurazione degli errori dipende dalla form a cui il controllo appartiene
    const innerHost = this.host.combogrid('textbox');
    if (this.control && (this.control.status === 'INVALID') && this.control.errors) {
      this.tooltipText = this.error.message(this.control.errors);
      this.tooltipElement.nativeElement.hidden = !this.isFocused;
      this.showTooltip = this.isFocused;
      innerHost.css({ border: '2px solid red', 'background-color': Globs.ERR_BACKGROUND });
    } else {
      this.tooltipElement.nativeElement.hidden = true;
      this.showTooltip = false;
      innerHost.css({ border: 'none', 'background-color': 'white' });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/easyui-ts/easyui-combogrid.component.ts